// cookie-notification.component.ts
import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AnalyticsConsentService } from '../../../core/services/analytics-consent.service';
import { SafeStorageService } from '@core/services/safe-storage.service';

/**
 * Cookie notification banner - now uses centralized AnalyticsConsentService
 * Previously used separate localStorage key 'cookieAccepted', now unified with marketing consent
 */
@Component({
  selector: 'app-cookie-notification',
  templateUrl: './cookie-notification.component.html',
  styleUrl: './cookie-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CookieNotificationComponent implements OnInit, OnDestroy {
  private consentService = inject(AnalyticsConsentService);
  private safeStorage = inject(SafeStorageService);
  private subscription?: Subscription;
  private static migrationComplete = false;

  cookieDecisionMade: boolean = false;

  ngOnInit(): void {
    this.cookieDecisionMade = this.consentService.hasDecisionBeenMade();

    // Migrate old localStorage key if it exists
    this.migrateOldConsentStorage();

    // Subscribe to consent changes
    this.subscription = this.consentService
      .onConsentChange()
      .subscribe(() => {
        // Any decision hides the banner. Setting this to `granted` brought the
        // banner back after "Decline" (#956), stacking it under the checkout bar.
        this.cookieDecisionMade = this.consentService.hasDecisionBeenMade();
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  acceptCookies() {
    this.consentService.setConsent(true);
    this.cookieDecisionMade = true;
  }

  declineCookies() {
    this.consentService.setConsent(false);
    this.cookieDecisionMade = true; // Hide banner after decision
  }

  /**
   * Migrate from old 'cookieAccepted' localStorage key to new consent service
   */
  private migrateOldConsentStorage(): void {
    if (CookieNotificationComponent.migrationComplete) {
      return;
    }
    CookieNotificationComponent.migrationComplete = true;

    try {
      const oldConsent = this.safeStorage.getItem('cookieAccepted');
      if (oldConsent !== null && !this.consentService.hasDecisionBeenMade()) {
        // If old consent exists but new doesn't, migrate it
        const wasAccepted = oldConsent === 'true';
        this.consentService.setConsent(wasAccepted);
        // Remove old key after migration
        this.safeStorage.removeItem('cookieAccepted');
      }
    } catch (_e) {
      // Ignore storage errors
    }
  }
}
